import axios from 'axios';
import { tokenStore } from './tokenStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Auth interceptor: add JWT token to all requests
api.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: handle 401 (token expired)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      tokenStore.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper to create authenticated axios for uploads (no default Content-Type)
const authUpload = (url, formData, options = {}) => {
  const token = tokenStore.get();
  return axios({
    url: `${API}${url}`,
    method: 'POST',
    data: formData,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    },
    onUploadProgress: options.onUploadProgress
  });
};

// Projects API
export const projectsApi = {
  getAll: () => api.get('/projects'),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`)
};

// Projector Library API
export const projectorsApi = {
  getLibrary: () => api.get('/projectors/library'),
  createCustom: (data) => api.post('/projectors/custom', data),
  deleteCustom: (id) => api.delete(`/projectors/custom/${id}`),
  // Persist the current user's lens list for a projector spec (built-in OR custom).
  // Replaces the spec's default lenses on subsequent library loads.
  saveSpecLenses: (specId, lenses) => api.put(`/projectors/spec-lenses/${specId}`, { lenses }),
  resetSpecLenses: (specId) => api.delete(`/projectors/spec-lenses/${specId}`)
};

// State auto-save (cloud)
export const stateApi = {
  save: (state) => api.post('/state/save', state),
  load: () => api.get('/state/load')
};

// Files API
export const filesApi = {
  getAll: () => api.get('/files'),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return authUpload('/files/upload', formData);
  },
  delete: (id) => api.delete(`/files/${id}`),
  move: (id, folderId) => api.put(`/files/${id}/move`, { folder_id: folderId }),
  getUrl: (path) => `${BACKEND_URL}${path}`,
  getServeUrl: (fileId) => `/api/files/serve/${fileId}`
};

// 3D Model Folders API (per-user folders for the 3D Files library)
export const modelFoldersApi = {
  getAll: () => api.get('/model-folders'),
  create: (name, color) => api.post('/model-folders', { name, color }),
  update: (id, data) => api.put(`/model-folders/${id}`, data),
  delete: (id) => api.delete(`/model-folders/${id}`)
};

// Content Library API
export const contentApi = {
  get: (folderId = null) => api.get('/content', { params: { folder_id: folderId } }),
  getAll: () => api.get('/content/all'),
  upload: (file, folderId = null, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) {
      formData.append('folder_id', folderId);
    }
    return authUpload('/content/upload', formData, {
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          onProgress(percent);
        }
      }
    });
  },
  createFolder: (name, parentId = null) => {
    const formData = new FormData();
    formData.append('name', name);
    if (parentId) {
      formData.append('parent_id', parentId);
    }
    return authUpload('/content/folder', formData);
  },
  delete: (id) => api.delete(`/content/${id}`),
  deleteFolder: (id) => api.delete(`/content/folder/${id}`),
  status: (id) => api.get(`/content/${id}/status`),
  getUrl: (itemId) => `/api/content/serve/${itemId}`,
  getServeUrl: (itemId) => `/api/content/serve/${itemId}`,
  // Variant-aware helpers for video assets
  getOptimizedUrl: (itemId) => `/api/content/serve/${itemId}?variant=optimized`,
  getThumbnailUrl: (itemId) => `/api/content/serve/${itemId}?variant=thumbnail`,
};

// ── Support Ticket API ───────────────────────────────────────────────
export const supportApi = {
  list: (status) => api.get('/support/tickets', { params: status ? { status } : {} }),
  get: (id) => api.get(`/support/tickets/${id}`),
  create: (payload) => api.post('/support/tickets', payload),
  reply: (id, payload) => api.post(`/support/tickets/${id}/messages`, payload),
  setStatus: (id, status) => api.patch(`/support/tickets/${id}`, { status }),
  unreadCount: () => api.get('/support/unread-count'),
};

export default api;
